import { memo, useEffect, useState } from "react";
import * as images from "@/components/images/Images";
import type { MenuItem } from "@/lib/getMenu";
import { cn } from "@/lib/utils";
import { normalizePath } from "@/utils";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import NavigationCard from "./NavigationCard";
import NavigationMobileSubmenuItem from "./NavigationMobileSubmenuItem";
import NavigationMobileTrigger from "./NavigationMobileTrigger";

export type NavigationMobileProps = {
  menuItems: MenuItem[] | null;
  ref?: React.Ref<HTMLElement | null>;
  onMenuClose: () => void;
  isMenuOpened?: boolean;
} & React.ComponentPropsWithoutRef<"nav">;

const NavigationMobile = ({
  menuItems,
  onMenuClose,
  isMenuOpened = false,
  className,
  ref,
}: NavigationMobileProps) => {
  const [openMainIndex, setOpenMainIndex] = useState<number | null>(null);
  const [openSubIndex, setOpenSubIndex] = useState<number | null>(null);

  const toggleMainMenu = (index: number) => {
    if (openMainIndex === index) {
      setOpenMainIndex(null);
      setOpenSubIndex(null);
    } else {
      setOpenMainIndex(index);
      setOpenSubIndex(null);
    }
  };

  const toggleSubMenu = (index: number) => {
    if (openSubIndex === index) {
      setOpenSubIndex(null);
    } else {
      setOpenSubIndex(index);
    }
  };

  useEffect(() => {
    if (isMenuOpened) {
      return;
    }

    setOpenMainIndex(null);
    setOpenSubIndex(null);
  }, [isMenuOpened]);

  return (
    <nav
      ref={ref}
      className={cn("flex flex-col gap-2 overflow-auto", className)}
    >
      {menuItems?.map((item, i) => {
        const submenuItems = item?.submenuItems ?? [];
        const submenuHasIcons = submenuItems?.some((item) => item.icon?.url);
        return (
          <div key={item.label}>
            {!submenuItems.length ? (
              <Link
                href={normalizePath(item.link)}
                className="block py-4 px-6 text-lg font-semibold"
                onClick={() => {
                  setOpenMainIndex(null);
                  setOpenSubIndex(null);
                }}
              >
                {item.label}
              </Link>
            ) : (
              <>
                <button
                  onClick={() => toggleMainMenu(i)}
                  className="w-full flex justify-between items-center py-4 px-6 text-lg leading-[150%] font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                  aria-label={`${openMainIndex === i ? 'Close' : 'Open'} ${item.label} menu`}
                  aria-expanded={openMainIndex === i}
                  title={`${openMainIndex === i ? 'Close' : 'Open'} ${item.label} menu`}
                >
                  {item.label}
                  <ChevronRight />
                </button>
                <div className="fixed inset-0 w-screen h-screen overflow-hidden pointer-events-none">
                  <div
                    className={cn(
                      "absolute inset-y-0 left-full w-screen h-screen bg-white transform transition-transform duration-300 ease-in-out pointer-events-auto flex flex-col translate-x-0",
                      {
                        "-translate-x-full":
                          openMainIndex === i && isMenuOpened,
                        "delay-300 duration-0": !isMenuOpened,
                      }
                    )}
                  >
                    <NavigationMobileTrigger
                      onBack={() => {
                        setOpenMainIndex(null);
                        setOpenSubIndex(null);
                      }}
                      onClose={onMenuClose}
                    >
                      {item.label}
                    </NavigationMobileTrigger>
                    <div className="p-6 flex-auto flex flex-col overflow-y-auto">
                      <ul className="grid grid-cols-1 gap-2 auto-rows-fr">
                        {submenuItems.map((sub, j) => {
                          const parentPath = normalizePath(sub.link);

                          return (
                            <NavigationMobileSubmenuItem
                              key={sub.title}
                              sub={sub}
                              index={j}
                              parentPath={parentPath}
                              openSubIndex={openSubIndex}
                              isMenuOpened={isMenuOpened}
                              toggleSubMenu={toggleSubMenu}
                              setOpenSubIndex={setOpenSubIndex}
                              onMenuClose={onMenuClose}
                            />
                          );
                        })}
                      </ul>
                      <div className="mt-6 border-t border-t-light-gray pt-6 flex flex-auto">
                        <div className="flex-auto min-w-0 rounded-2xl bg-semantic-foreground-soft py-6 px-4 flex items-center justify-center">
                          <NavigationCard
                            imageUrl={
                              submenuHasIcons ? images.menubg1 : images.menubg2
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default memo(NavigationMobile);
